// functions/lib/ugc/memberRate.ts
//
// Montant pour 1 000 vues fixé par l'équipe pour chaque membre (migration 0021) : qui attend son
// montant, et la saisie en euros de l'admin (« 2,50 ») convertie en centimes. Module pur, sans
// base ni réseau : l'admin, le serveur et le robot l'importent.

import type { MemberStatus } from '../../../src/lib/ugcTypes'
import { UGC_RULES, amountCents, formatEuros, isValidMemberRate, memberRules } from './rules'

/**
 * Membre dont le montant pour 1 000 vues reste à fixer : actif ou en pause (conditions acceptées,
 * vidéos possibles), sans montant. Un invité n'a pas encore accepté les conditions, un parti n'en
 * a plus besoin.
 */
export function needsRate(m: { status: MemberStatus; rateCentsPerThousand: number | null }): boolean {
  return (m.status === 'active' || m.status === 'paused') && m.rateCentsPerThousand === null
}

/**
 * Saisie en euros de l'admin → centimes : « 2,50 », « 2.5 », « 2 », « 2,50 € » ; au plus deux
 * décimales, entre les bornes de `isValidMemberRate`. `null` pour toute autre saisie.
 */
export function parseRateInput(text: string): number | null {
  const cleaned = text.replace(/[\s  ]/g, '').replace(/€$/u, '').replace(',', '.')
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(cleaned)
  if (!match) return null
  const euros = Number(match[1])
  const cents = Number((match[2] ?? '').padEnd(2, '0'))
  const total = euros * 100 + cents
  return isValidMemberRate(total) ? total : null
}

/** Centimes → saisie en euros à la française, toujours deux décimales (« 2,50 »). */
export function formatRateInput(cents: number): string {
  return `${Math.floor(cents / 100)},${String(cents % 100).padStart(2, '0')}`
}

/** Au-delà de 10 € pour 1 000 vues, l'admin confirme avant d'enregistrer (faute de frappe probable). */
export const HIGH_RATE_CENTS = 1_000

/**
 * Question de confirmation d'un montant élevé : « 25,00 € pour 1 000 vues, soit jusqu'à 3 750,00 €
 * par vidéo : confirmer ? », ou `null` jusqu'à 10 € pour 1 000 vues.
 */
export function highRateQuestion(cents: number): string | null {
  if (cents <= HIGH_RATE_CENTS) return null
  const perVideo = amountCents(UGC_RULES.capViews, memberRules(cents))
  return `${formatEuros(cents)} pour 1\u00a0000 vues, soit jusqu'à ${formatEuros(perVideo)} par vidéo : confirmer ?`
}
